// src/services/workloadSettings.service.js
import { api } from './api';

class WorkloadSettingsService {
    constructor() {
        this.storageKey = 'oakhr_workload_settings';
        this.defaultSettings = {
            light: { min: 0, max: 19 },
            moderate: { min: 20, max: 39 },
            heavy: { min: 40, max: 999 }
        };
    }

    /**
     * Get workload settings - tries database first, falls back to localStorage
     */
    async getWorkloadSettings() {
        // Try database first
        try {
            const response = await api.get('/api/workload-settings');
            console.log('✅ Workload settings loaded from database');
            return response.data;
        } catch (error) {
            console.log('⚠️ Database not available, using localStorage fallback');
            // Fall back to localStorage
            try {
                const savedSettings = localStorage.getItem(this.storageKey);
                if (savedSettings) {
                    const settings = JSON.parse(savedSettings);
                    console.log('✅ Workload settings loaded from localStorage');
                    return settings;
                }
            } catch (localError) {
                console.error('Error reading from localStorage:', localError);
            }

            // Return default settings if both fail
            console.log('📋 Using default workload settings');
            return this.defaultSettings;
        }
    }

    /**
     * Save workload settings - tries database first, falls back to localStorage
     */
    async saveWorkloadSettings(settings) {
        // Try database first
        try {
            const response = await api.post('/api/workload-settings', settings);
            console.log('✅ Workload settings saved to database');
            return response.data;
        } catch (error) {
            console.log('⚠️ Database not available, saving to localStorage fallback');

            // Fall back to localStorage
            try {
                localStorage.setItem(this.storageKey, JSON.stringify(settings));
                console.log('✅ Workload settings saved to localStorage');
                return { success: true, data: settings, source: 'localStorage' };
            } catch (localError) {
                console.error('Error saving to localStorage:', localError);
                throw new Error('Failed to save settings to both database and localStorage');
            }
        }
    }

    /**
     * Update workload settings - tries database first, falls back to localStorage
     */
    async updateWorkloadSettings(settings) {
        // Try database first
        try {
            const response = await api.put('/api/workload-settings', settings);
            console.log('✅ Workload settings updated in database');
            return response.data;
        } catch (error) {
            console.log('⚠️ Database not available, updating localStorage fallback');

            // Fall back to localStorage
            try {
                localStorage.setItem(this.storageKey, JSON.stringify(settings));
                console.log('✅ Workload settings updated in localStorage');
                return { success: true, data: settings, source: 'localStorage' };
            } catch (localError) {
                console.error('Error updating localStorage:', localError);
                throw new Error('Failed to update settings in both database and localStorage');
            }
        }
    }

    /**
     * Reset workload settings to defaults
     */
    async resetWorkloadSettings() {
        // Try database first
        try {
            const response = await api.post('/api/workload-settings/reset');
            console.log('✅ Workload settings reset in database');
            return response.data;
        } catch (error) {
            console.log('⚠️ Database not available, resetting localStorage fallback');

            // Fall back to localStorage
            try {
                localStorage.setItem(this.storageKey, JSON.stringify(this.defaultSettings));
                console.log('✅ Workload settings reset in localStorage');
                return { success: true, data: this.defaultSettings, source: 'localStorage' };
            } catch (localError) {
                console.error('Error resetting localStorage:', localError);
                throw new Error('Failed to reset settings in both database and localStorage');
            }
        }
    }

    /**
     * Get current storage method being used
     */
    getStorageMethod() {
        try {
            const savedSettings = localStorage.getItem(this.storageKey);
            return savedSettings ? 'localStorage' : 'database';
        } catch {
            return 'database';
        }
    }
}

const workloadSettingsService = new WorkloadSettingsService();
export default workloadSettingsService;